import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FlashbarProps } from "@amzn/awsui-components-react";

const FLASHBAR_STYLES = {
  position: "fixed",
  bottom: "2vh",
  right: "2vw",
  width: "30vw",
  minWidth: "300px",
  maxWidth: "600px",
  zIndex: 1000,
  fontSize: "calc(14px + 0.3vw)",
} as const;
export function useFolderUI(isSummaryView = false) {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState("");
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleModalDismiss = useCallback(() => {
    setIsModalVisible(false);
    setSelectedFolderId("");
    setError("");
  }, []);

  const handleCreateFolder = useCallback(() => {
    setIsFolderModalVisible(true);
    setFolderName("");
    setError("");
  }, []);

  const handleFolderModalDismiss = useCallback(() => {
    setIsFolderModalVisible(false);
    setFolderName("");
    setError("");
  }, []);

  const handleSaveFolder = useCallback(
    (saveFolder: (name: string) => string | void) => {
      if (!folderName.trim()) {
        setError("Please enter a folder name");
        return;
      }

      let newFolderId: string | void;
      try {
        newFolderId = saveFolder(folderName);
      } catch (e) {
        // Keep the window open with the reason, e.g. a duplicate name.
        setError(
          e instanceof Error ? e.message : "The folder could not be created."
        );
        return;
      }
      // Select the new folder so the user can add to it straight away
      // instead of picking it from the dropdown (TRACK-7418).
      if (newFolderId) {
        setSelectedFolderId(newFolderId);
      }
      setIsFolderModalVisible(false);
      setFolderName("");
      setError("");
      setNotification({
        type: "success",
        message: "Folder created successfully",
      });
    },
    [folderName]
  );

  const handleNavigateToFolders = useCallback(() => {
    navigate("/saved", { replace: true });
  }, [navigate]);

  const getFlashbarItems =
    useCallback((): FlashbarProps.MessageDefinition[] => {
      if (!notification) return [];

      return [
        {
          type: notification.type,
          content: notification.message,
          dismissible: true,
          onDismiss: () => setNotification(null),
          buttonText:
            notification.type === "success" ? "Go to Folders" : undefined,
          onButtonClick:
            notification.type === "success"
              ? () => {
                  if (isSummaryView) {
                    handleNavigateToFolders();
                    setTimeout(() => window.location.reload(), 100);
                  } else {
                    handleNavigateToFolders();
                  }
                }
              : undefined,
        },
      ];
    }, [notification, handleNavigateToFolders, isSummaryView]);

  return useMemo(
    () => ({
      isModalVisible,
      isFolderModalVisible,
      selectedFolderId,
      folderName,
      error,
      notification,
      setIsModalVisible,
      setIsFolderModalVisible,
      setSelectedFolderId,
      setFolderName,
      setError,
      setNotification,
      handleModalDismiss,
      handleCreateFolder,
      handleFolderModalDismiss,
      handleSaveFolder,
      handleNavigateToFolders,
      getFlashbarItems,
      flashbarStyles: FLASHBAR_STYLES,
    }),
    [
      isModalVisible,
      isFolderModalVisible,
      selectedFolderId,
      folderName,
      error,
      notification,
      handleModalDismiss,
      handleCreateFolder,
      handleFolderModalDismiss,
      handleSaveFolder,
      handleNavigateToFolders,
      getFlashbarItems,
    ]
  );
}
